// src/components/DockNav.js
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, User, Mail, Facebook, LogOut, MessageCircle, Search, ChartNoAxesCombinedIcon, Lock } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../AuthContext';
import './DockNav.css';
import { collectionGroup, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import GlobalSearchModal from './GlobalSearchModal';

const items = [
  {
    label: 'Dashboard',
    icon: <Home size={24} />,
    path: '/',
    roles: ['admin'],
  },
  {
    label: 'Search',
    icon: <Search size={24} />,
    action: 'search',
    roles: ['admin'],
  },
  {
    label: 'Clients',
    icon: <User size={24} />,
    path: '/clients',
    roles: ['admin', 'facebookAdmin'],
  },
  {
    label: 'Orders',
    icon: <Mail size={24} />,
    path: '/orders',
    roles: ['admin'],
  },
  {
    label: 'Facebook⁺',
    icon: <Facebook size={24} />,
    path: '/facebook-events',
    roles: ['admin', 'facebookAdmin', 'facebook'],
  },
  {
    label: 'Messages',
    icon: <MessageCircle size={24} />,
    path: '/messages',
    roles: ['admin'],
  },
  {
    label: 'Analytics',
    icon: <ChartNoAxesCombinedIcon size={24} />,
    path: '/analytics',
    roles: ['admin'],
  },
    {
    label: 'Assets',
    icon: <Lock size={24} />,
    path: '/assets',
    roles: ['admin'],
  },
];

export default function DockNav() {
  const { userData } = useAuth();
  const role = userData?.role;
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showSearchModal, setShowSearchModal] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Logout failed', error);
    }
  };

  const handleItemClick = (item) => {
    if (item.action === 'search') {
      setShowSearchModal(true);
    } else if (item.path) {
      navigate(item.path);
    }
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribeAuth = auth.onAuthStateChanged(user => {
      if (!user || !userData?.role) return;

      const currentUid = user.uid;

      const unsubscribeMessages = onSnapshot(collectionGroup(db, 'messages'), (snapshot) => {
        const messages = snapshot.docs
          .map(d => {
            const parentColl = d.ref.parent;
            const clientDocRef = parentColl.parent;
            if (!clientDocRef) return null;
            return d.data();
          })
          .filter(Boolean);

        const unread = messages.filter(m =>
          m.read !== true &&
          ['admin', 'internal'].includes(m.recipientType) &&
          m.senderId !== currentUid
        );

        setUnreadCount(unread.length);
      });

      return () => unsubscribeMessages();
    });

    return () => unsubscribeAuth();
  }, [userData?.role]);

  // Handle keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearchModal(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      <div className="dock-nav">
        {items
          .filter(item => item.roles.includes(role))
          .map(item => (
            <button
              key={item.path || item.action}
              onClick={() => handleItemClick(item)}
              className={`dock-button ${
                item.path && location.pathname === item.path ? 'active' : ''
              } ${item.action === 'search' ? 'search-button' : ''}`}
              title={item.action === 'search' ? 'Search (⌘K)' : item.label}
            >
              <div className="icon-wrapper">
                {item.icon}
                {item.label === 'Messages' && unreadCount > 0 && (
                  <span className="notification-badge">{unreadCount}</span>
                )}
              </div>
              <span className="dock-label">{item.label}</span>
            </button>
          ))}

        {/* Logout Button (always shown if logged in) */}
        <button onClick={handleLogout} className="dock-button logout-button">
          <LogOut size={24} />
          <span className="dock-label">Logout</span>
        </button>
      </div>

      {/* Global Search Modal */}
      <GlobalSearchModal 
        isOpen={showSearchModal} 
        onClose={() => setShowSearchModal(false)} 
      />
    </>
  );
}